"use client";

import { Avatar, Typography } from "antd/lib";
import useMyQuery from "@/hooks/useMyQuery";
import type { TokenData } from "@/models/token";
import PriceChange from "../PriceChange";
import MyTable from "../MyTable";
import type { MyColumnType } from "../MyTable";
import { CLOUDFRONT_URL } from "@/constant/apiPath";
import { formatThousands } from "@/helpers";
import { useCurrencyContext } from "@/context/CurrencyContext";
import { useLanguageContext } from "@/context/LanguageContext";

interface TokenListProps {
  title: string;
  url: string;
}

export default function TokenList({ title, url }: TokenListProps) {
  const { currentCurrency } = useCurrencyContext();
  const { data, isLoading } = useMyQuery<TokenData[]>(
    {
      url: `${CLOUDFRONT_URL}${url}`,
      includeParamsInQueryKey: true,
    },
    { queryKey: ["token-list", url] },
  );
  const { language } = useLanguageContext();

  const columns: MyColumnType<TokenData>[] = [
    {
      title: "TICKET",
      key: "ticket",
      dataIndex: "symbol",
      width: 150,
      render: (_, record) => (
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => window.open(`/${language}/markets/${record?.symbol}`, "_blank")}
        >
          <Avatar src={record.logo_uri} size={24} />
          <Typography.Text strong>{record.symbol}</Typography.Text>
        </div>
      ),
    },
    {
      title: "PRICE",
      key: "price",
      dataIndex: "price",
      sorter: (a, b) => a.price - b.price,
      render: (_, record) => (
        <div className="whitespace-nowrap">
          <Typography.Text>{formatThousands(record.price, currentCurrency)}</Typography.Text>
          <Typography.Text className="ml-2">
            (<PriceChange price={record.price_change?.change_1D?.percentage ?? 0} />)
          </Typography.Text>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full border-[#e5e7eb] border-solid border rounded-lg overflow-hidden">
      <div className="p-4">
        <Typography.Title level={5} className="m-0">
          {title}
        </Typography.Title>
      </div>
      <MyTable
        columns={columns}
        dataSource={data?.slice(0,5) ?? []}
        pagination={false}
        isLoading={isLoading}
        className="rounded-b-lg"
      />
    </div>
  );
}

